import { Trans } from '@lingui/macro'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Button } from '@mui/material'
import { FrameworkComponentProps } from '@om1/platform-utils'
import React, { useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { cohortBlocksEditActions, CohortDraggingState, CohortTreeActionType, CriteriaOperation, CriteriaType, OperationNode } from '../../../state'
import { CRITERIA_DEFAULTS } from '../base/CohortEditComponent'
import { useCriteriaDropZone } from '../utils/useCriteriaDropZone'
import { CriteriaSelectionMenu } from './CriteriaSelectionMenu'

export type OrDropZoneProps = FrameworkComponentProps<
    { node: OperationNode; dragState: CohortDraggingState },
    typeof cohortBlocksEditActions,
    { showCriteriaSelection?: boolean }
>

/**
 * A drop zone displayed in a criteria block when an element is being dragged, which allows adding new criterion to the
 * block.
 */
export const OrDropZone: React.FunctionComponent<OrDropZoneProps> = ({ state, actions, props: { showCriteriaSelection = false } }) => {
    const ref = useRef<HTMLDivElement>(null)
    const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null)
    const menuOpen = Boolean(menuAnchor)

    const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchor(event.currentTarget)
    const handleMenuClose = () => setMenuAnchor(null)

    const addOrCriteria = (type: CriteriaType, uuid: string) => {
        // This is the core logic: first, ensure the block's operation is OR.
        actions.operationUpdate({
            target: { nodeId: state.node.id },
            operation: { operation: CriteriaOperation.OR }
        })

        if (Object.keys(CRITERIA_DEFAULTS).includes(type)) {
            // Then, insert the new criterion into the block.
            actions.criteriaInsert({
                target: { nodeId: state.node.id },
                criteriaType: type,
                filters: CRITERIA_DEFAULTS[type],
                uuid
            })
        } else {
            actions.criteriaDialogTrigger({ type, action: { type: CohortTreeActionType.Insert, target: { nodeId: state.node.id }, uuid } })
        }
    }

    const { dropProps, isDropTarget } = useCriteriaDropZone({
        ref,
        onAdd: (type) => addOrCriteria(type, uuidv4()),
        onCopy: (source) => actions.criteriaCopy({ source, target: { nodeId: state.node.id } }),
        onMove: (source) => actions.criteriaMove({ source, target: { nodeId: state.node.id } })
    })

    const handleAddCriteria = (criteriaType: CriteriaType) => {
        addOrCriteria(criteriaType, uuidv4())
    }

    return (
        <Box width='100%'>
            <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                visibility={state.dragState.active ? 'visible' : 'hidden'}
                height={state.dragState.active ? 38 : 0}
                overflow='hidden'
                sx={{ transition: 'all 150ms ease-in-out' }}
            >
                <Box height={10} width='1px' bgcolor='secondary.light' />
                <span>
                    <Trans>OR</Trans>
                </span>
                <Box height={10} width='1px' bgcolor='secondary.light' />
            </Box>
            <Box display='flex' alignItems='center' gap={1} width='100%'>
                {!state.dragState.active && (
                    <Box mt={1} display='flex' gap={1}>
                        <Button
                            type='button'
                            variant='text'
                            onClick={handleMenuOpen}
                            data-testid='criteria-block-add-or-criteria'
                            startIcon={<AddCircleIcon />}
                        >
                            <Trans>OR</Trans>
                        </Button>
                    </Box>
                )}
                {state.dragState.active && (
                    <Box
                        // Drag and drop
                        ref={ref}
                        role='button'
                        tabIndex={0}
                        {...dropProps}
                        // Styles
                        border={2}
                        borderColor='primary.light'
                        overflow='hidden'
                        height={54}
                        bgcolor={isDropTarget ? '#eee' : 'transparent'}
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                        flex={1}
                        sx={{ borderStyle: 'dashed', transition: 'all 150ms ease-in-out' }}
                    >
                        <Trans>Drag and drop here to add criteria</Trans>
                    </Box>
                )}
            </Box>

            <CriteriaSelectionMenu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                onCriteriaSelect={handleAddCriteria}
                testIdPrefix='criteria-block-menu-item'
            />
        </Box>
    )
}
